import { useState } from "react";
import clsx from "clsx";
import { useCurrentUser } from "@/lib/auth";
import { useErrorLogSummary } from "@/hooks/useErrorLogSummary";
import { ErrorLogsDialog } from "@/features/errorLogs/ErrorLogsDialog";

/**
 * Kleines App-Gesundheits-Symbol neben dem WebSocket-Punkt (nur App-Admins).
 *
 * Grün = keine offenen Server-Fehler, gelb = nur Warnungen/Unkritisches,
 * rot (pulsierend) = mindestens ein kritischer Fehler. Klick öffnet die
 * Fehler-Details.
 */
export function AppHealthIndicator({ className }: { className?: string }) {
  const { user } = useCurrentUser();
  const { summary, isAdmin } = useErrorLogSummary(user);
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!isAdmin || !summary) return null;

  const status = summary.status ?? "ok";
  const meta =
    status === "kritisch"
      ? {
          color: "bg-status-red",
          pulse: true,
          label: `Kritische Server-Fehler: ${summary.severity_counts?.kritisch ?? 0} offen`,
        }
      : status === "warnung"
        ? {
            color: "bg-status-orange",
            pulse: false,
            label: `Server-Warnungen: ${summary.severity_counts?.warnung ?? 0} offen`,
          }
        : status === "unkritisch"
          ? {
              color: "bg-status-orange/60",
              pulse: false,
              label: `Unkritische Server-Fehler: ${summary.severity_counts?.unkritisch ?? 0} offen`,
            }
          : { color: "bg-status-green", pulse: false, label: "App-Status: keine offenen Fehler" };

  return (
    <>
      <button
        type="button"
        onClick={() => setDialogOpen(true)}
        className={clsx(
          "inline-flex h-6 w-6 items-center justify-center rounded-full transition hover:bg-fill",
          className,
        )}
        title={meta.label}
        aria-label={`${meta.label} – Details öffnen`}
      >
        <span className="relative inline-flex h-2.5 w-2.5">
          {meta.pulse ? (
            <span
              className={clsx(
                "absolute inline-flex h-full w-full animate-ping rounded-full opacity-75",
                meta.color,
              )}
            />
          ) : null}
          <span className={clsx("relative inline-flex h-2.5 w-2.5 rounded-full", meta.color)}>
            <HeartbeatIcon />
          </span>
        </span>
      </button>
      <ErrorLogsDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </>
  );
}

function HeartbeatIcon() {
  return (
    <svg
      viewBox="0 0 10 10"
      className="absolute inset-0 h-full w-full text-white"
      aria-hidden
    >
      <path
        d="M1 5h2l1-2 2 4 1-2h2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
